/**
 * Mirrors server/lib/runningWorkoutStructure.flattenStructureForViz for client-side previews.
 */

function inferSplitMeasureModeFromStoredSeg(seg) {
  const explicit = String(seg.split_measure_mode || '').toLowerCase();
  if (explicit === 'distance') return 'distance';
  if (explicit === 'duration') return 'duration';
  const d = Number(seg.duration_sec) || 0;
  const dm = seg.distance_m != null ? Number(seg.distance_m) : 0;
  if (dm > 0 && d <= 0) return 'distance';
  return 'duration';
}

export function flattenStructureForViz(structure) {
  if (!structure || !Array.isArray(structure.groups)) return [];
  const out = [];
  for (const g of structure.groups) {
    const cycles = Math.max(1, Number(g.cycles) || 1);
    const segs = g.segments || [];
    for (let c = 0; c < cycles; c++) {
      for (const seg of segs) {
        const d = Number(seg.duration_sec) || 0;
        const ic = (seg.run_intensity_class || seg.role || 'work').toLowerCase();
        let norm = 0.85;
        if (seg.cp_percent != null) norm = Math.min(1.25, Math.max(0.35, Number(seg.cp_percent) / 100));
        else if (seg.rpe != null) norm = Math.min(1, Math.max(0.3, Number(seg.rpe) / 10));
        else if (ic === 'rest' || ic === 'warmup' || ic === 'cooldown') norm = 0.72;

        const sm = inferSplitMeasureModeFromStoredSeg(seg);
        out.push({
          duration_sec: d,
          distance_m: seg.distance_m != null ? Number(seg.distance_m) : undefined,
          split_measure_mode: sm,
          distance_quantity:
            seg.distance_quantity != null && Number.isFinite(Number(seg.distance_quantity))
              ? Number(seg.distance_quantity)
              : undefined,
          distance_unit_selected:
            seg.distance_unit_selected != null ? String(seg.distance_unit_selected) : undefined,
          run_intensity_class: ic,
          intensity_norm: norm,
          cp_percent: seg.cp_percent != null ? Number(seg.cp_percent) : null,
          cp_percent_min: seg.cp_percent_min != null ? Number(seg.cp_percent_min) : null,
          cp_percent_max: seg.cp_percent_max != null ? Number(seg.cp_percent_max) : null,
          intensity_kind: seg.intensity_kind != null ? String(seg.intensity_kind) : null,
          endless: !!(seg.endless || seg.flexible),
        });
      }
    }
  }
  return out;
}

function parseParamsLoose(params) {
  if (params == null) return {};
  if (typeof params === 'object') return params;
  try {
    return typeof params === 'string' ? JSON.parse(params) : {};
  } catch {
    return {};
  }
}

/** Client mirror of server `resolveRunPreviewSplits`. */
export function resolveRunPreviewSplits(params, structure) {
  const p = parseParamsLoose(params);
  const fromStruct = flattenStructureForViz(structure);
  const cached = Array.isArray(p.run_preview_splits) ? p.run_preview_splits : [];
  if (fromStruct.length === 0) return cached;
  if (cached.length === 0) return fromStruct;
  if (cached.length === fromStruct.length) {
    return fromStruct.map((s, i) => ({
      ...s,
      endless: !!(s.endless || cached[i]?.endless),
    }));
  }
  return fromStruct;
}

export function firstRunningStructureFromSession(session) {
  const blocks = session?.blocks || [];
  for (const b of blocks) {
    const st = b?.library_exercise?.structure;
    if (st?.groups?.length) return st;
    const so = b?.structure_override;
    if (so?.groups?.length) return so;
  }
  return null;
}
